import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Building from 'components/icons/Building';
import Folder from 'components/icons/Folder';
import TrendingUp from 'components/icons/TrendingUp';
import Heading from 'components/shared/heading';
import styles from './styles.module.scss';

// Per-card icon: a value mapping to a project icon (same idea as IconsRow's getIcon).
// Curated to the three Who-It's-For personas for now; add more from src/components/icons/
// as needed. Empty/unknown → no icon.
const ICONS = {
  building: Building,
  folder: Folder,
  'trending-up': TrendingUp,
};

const getIcon = (name) => ICONS[name] || null;

const LabelCards = ({
  eyebrow = '',
  title = null,
  description = '',
  theme = '',
  labelStyle = 'badge',
  cards = [],
}) => {
  if (!title && (!cards || cards.length === 0)) {
    return null;
  }

  return (
    <section
      className={cn(styles.root, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      <div className="container">
        {(eyebrow || title || description) && (
          <div className={styles.header}>
            {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
            {title && <Heading tag="h2" size="xl" className={styles.title} innerHTML={title} />}
            {description && (
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>
        )}

        {cards && cards.length > 0 && (
          <div className={styles.grid}>
            {cards.map((card, index) => {
              const Icon = getIcon(card.icon);
              return (
                <div key={index} className={styles.card}>
                  {Icon && (
                    <span className={styles.iconBox}>
                      <Icon aria-hidden="true" />
                    </span>
                  )}
                  {card.label && (
                    <span
                      className={cn(styles.label, {
                        [styles.labelKicker]: labelStyle === 'kicker',
                      })}
                    >
                      {card.label}
                    </span>
                  )}
                  {card.title && (
                    <Heading tag="h3" size="md" className={styles.cardTitle}>
                      {card.title}
                    </Heading>
                  )}
                  {card.quote && (
                    <div
                      className={styles.quote}
                      dangerouslySetInnerHTML={{ __html: card.quote }}
                    />
                  )}
                  {card.text && (
                    <div
                      className={styles.cardText}
                      dangerouslySetInnerHTML={{ __html: card.text }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

LabelCards.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string, // HTML; wrap words in <span>/<strong> to color them
  description: PropTypes.string,
  theme: PropTypes.oneOf(['', 'light', 'dark']),
  // 'badge' = filled accent chip (THE PROBLEM); 'kicker' = plain uppercase role (WHO IT'S FOR)
  labelStyle: PropTypes.oneOf(['badge', 'kicker']),
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string, // maps to a project icon (see ICONS above); optional
      label: PropTypes.string, // highlighted badge / uppercase kicker above the card
      title: PropTypes.string,
      quote: PropTypes.string, // HTML; bold italic emphasis line (WHO IT'S FOR)
      text: PropTypes.string, // HTML
    })
  ),
};

export default LabelCards;
